import type { DateRange } from "@mui/x-date-pickers-pro"
import type { Dayjs } from "dayjs"
import { useState } from "react"
import { objectEntries } from "../../../../utils/helpers"
import { dayjsUTC } from "../../../../utils/dates"
import { ChartWithFilters } from "../../../Charts/ChartWithFilters"
import { WASTEWATER_TARGET_GROUP_COLORS } from "../../timeSeriesConstants"
import { useWastewaterData } from "../../useWastewaterData"
import { TARGET_GROUPS } from "../../WastewaterUtils"
import { DataSourceTabLayout } from "./DataSourceTabLayout"
import { WastewaterTimeSeries } from "./WastewaterTimeSeries"

const PHC_SITE_ID = "201 HI JBPHH"

const LABEL_TOOLTIP_TEXT: Record<keyof typeof TARGET_GROUPS, string> = {
  "Respiratory Diseases":
    "This chart plots concentrations of respiratory targets quantified using dPCR, which are normalized using a marker of human fecal activity and suppressed to 0 when values fall below the limit of detection (LOD).",
  "Gastrointestinal Diseases":
    "This chart plots concentrations of gastrointestinal targets quantified using dPCR, which are normalized using a marker of human fecal activity and suppressed to 0 when values fall below the limit of detection (LOD).",
  "Other Diseases":
    "This chart plots concentrations of other viral targets quantified using dPCR, which are normalized using a marker of human fecal activity. Values below the limit of detection (LOD) are considered to monitor potential activity under this threshold for these more rare or novel threats.",
}

export const WastewaterDataTab: React.FC = () => {
  return (
    <DataSourceTabLayout
      aboutContent={[
        "Wastewater surveillance uses untreated sewage to provide information on community-level transmission of certain pathogens that are shed in toilets, sinks, showers, or other drains by infected individuals or other animals, including asymptomatic or pre-symptomatic cases. This source can provide early warning of changing disease dynamics and unlike clinical data, it does not rely on patients seeking medical care to be captured.",
        "This data should be interpreted with caution and in the context of other supporting sources, as wastewater data can suffer from reporting lags and be influenced by environmental conditions, population movement, sewershed coverage, and variable shedding rates.",
      ]}
    >
      {objectEntries(TARGET_GROUPS).map(([groupName, targets]) => {
        return (
          <WastewaterChart
            key={groupName}
            groupName={groupName}
            targets={targets}
          />
        )
      })}
    </DataSourceTabLayout>
  )
}

export const WastewaterChart = ({
  groupName,
  targets,
}: {
  groupName: keyof typeof TARGET_GROUPS
  targets: string[]
}) => {
  const [dateRange, setDateRange] = useState<DateRange<Dayjs>>([
    dayjsUTC().subtract(6, "month"),
    dayjsUTC(),
  ])

  const { activityLevels: chartData, anomalyKeys } = useWastewaterData({
    phcSiteId: PHC_SITE_ID,
    limit: 10000,
    startDate: dayjsUTC(dateRange[0]?.format("YYYY-MM-DD")),
    endDate: dayjsUTC(dateRange[1]?.format("YYYY-MM-DD")),
  })

  const groupData = chartData.filter(
    d =>
      targets.includes(d.target_name) &&
      dayjsUTC(d.sampling_end_date).isAfter(dayjsUTC().subtract(6, "month"))
  )

  const isRare = groupName === "Other Diseases"
  const normalizedValueKey = isRare
    ? "fecal_normalized_target_copies_per_l"
    : "fecal_normalized_target_copies_per_l_lod"

  const normalizedTitle = isRare
    ? "FECAL NORMALIZED TARGET (COPIES/L)"
    : "FECAL NORMALIZED TARGET (COPIES/L) LOD"

  const tooltipContent = LABEL_TOOLTIP_TEXT[groupName]

  return (
    <ChartWithFilters
      title={groupName}
      tooltipContent={tooltipContent}
      filters={{ dateRange, onDateRangeChange: setDateRange }}
    >
      <WastewaterTimeSeries
        data={groupData}
        valueKey={normalizedValueKey}
        title={normalizedTitle}
        colors={[...WASTEWATER_TARGET_GROUP_COLORS[groupName]]}
        anomalyKeys={anomalyKeys}
      />
    </ChartWithFilters>
  )
}
